import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';

const LOGO_URL = "/logo.svg";




const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

export default function TermsOfUsePage() {
  return (
    <div data-testid="terms-of-use-page">
      <Navbar />
      <SEOHead title="Terms of Use" description="ChannelLoyalty terms of use. Terms and conditions governing the use of our B2B channel loyalty platform and services." canonical="https://channelloyalty.ai/terms-of-use" />
      
      <main>
        <section className="section pt-32">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Use</h1>
              
              <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
                <p>
                  These Terms of Use ("Terms") govern access to and use of the website, applications, platforms, and services (collectively, the "Services") provided by ChannelLoyalty ("ChannelLoyalty", "we", "us", or "our"). By accessing or using the Services, you agree to be bound by these Terms. If you do not agree, you must not access or use the Services.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">1. Eligibility</h2>
                <p>You represent and warrant that:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You are at least 18 years of age, and</li>
                  <li>You are legally competent to enter into a binding agreement under applicable law.</li>
                </ul>
                <p>Use of the Services may be restricted or conditional based on regulatory, contractual, or program-specific requirements.</p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">2. Scope of Services</h2>
                <p>
                  ChannelLoyalty provides technology solutions for QR-based authentication, loyalty programs, rewards, payouts, and related services for brands, distributors, retailers, and end users. The availability, features, and eligibility for earning or redeeming rewards may vary by program, partner, geography, or regulatory requirement.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">3. User Obligations</h2>
                <p>By using the Services, you agree to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate, complete, and current information during registration and usage</li>
                  <li>Use the Services only for lawful purposes</li>
                  <li>Not attempt to manipulate, misuse, or defraud any reward, payout, or verification mechanism</li>
                  <li>Maintain confidentiality of any credentials or access provided to you</li>
                </ul>
                <p>ChannelLoyalty reserves the right to suspend or terminate access in case of violation.</p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">4. KYC, Verification & Compliance</h2>
                <p>Certain Services may require identity verification, including KYC checks, as mandated by applicable laws or program rules. You consent to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Submission and verification of identity information</li>
                  <li>Processing of such data through authorized third-party service providers</li>
                  <li>Temporary or permanent restriction of Services if verification fails or is incomplete</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">5. Rewards, Points & Redemptions</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Rewards, points, or benefits have no intrinsic monetary value unless expressly stated</li>
                  <li>Accrual and redemption are subject to program-specific rules, limits, and validity periods</li>
                  <li>ChannelLoyalty does not guarantee availability of specific rewards at all times</li>
                  <li>Fraudulent, duplicate, or erroneous transactions may be reversed</li>
                  <li>ChannelLoyalty's determination regarding rewards and redemptions shall be final</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">6. Payments & Payouts</h2>
                <p>Where applicable:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Payouts are processed through regulated payment mechanisms (e.g., UPI, vouchers)</li>
                  <li>Processing timelines may vary due to banking, regulatory, or third-party dependencies</li>
                  <li>ChannelLoyalty shall not be liable for delays caused by external payment systems</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">7. Intellectual Property Rights</h2>
                <p>
                  All intellectual property associated with the Services, including software, trademarks, logos, content, and designs, are owned by or licensed to ChannelLoyalty. You are granted a limited, non-exclusive, non-transferable right to use the Services solely for their intended purpose. No rights are granted beyond those expressly stated.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">8. Data Privacy</h2>
                <p>
                  Use of the Services is subject to ChannelLoyalty's Privacy Policy and Security & Compliance framework. You acknowledge and consent to the collection, processing, storage, and use of data as required for service delivery, compliance, analytics, and legal obligations.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">9. Third-Party Services</h2>
                <p>The Services may integrate or link to third-party platforms, vendors, or partners. ChannelLoyalty:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Does not control such third-party services</li>
                  <li>Is not responsible for their availability, accuracy, or actions</li>
                  <li>Disclaims liability arising from third-party products or services</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">10. Disclaimer of Warranties</h2>
                <p>
                  The Services are provided on an "as is" and "as available" basis. To the maximum extent permitted by law, ChannelLoyalty disclaims all warranties, whether express or implied, including fitness for a particular purpose, accuracy, and uninterrupted availability.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">11. Limitation of Liability</h2>
                <p>To the fullest extent permitted by law:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>ChannelLoyalty shall not be liable for indirect, incidental, special, or consequential damages</li>
                  <li>Aggregate liability, if any, shall not exceed the fees paid (if applicable) for the specific Service giving rise to the claim</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">12. Indemnification</h2>
                <p>You agree to indemnify and hold harmless ChannelLoyalty, its affiliates, officers, and partners from any claims, losses, or liabilities arising from:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your misuse of the Services</li>
                  <li>Violation of these Terms</li>
                  <li>Breach of applicable laws or third-party rights</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">13. Suspension & Termination</h2>
                <p>ChannelLoyalty may suspend or terminate access to the Services:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>For violation of these Terms</li>
                  <li>To comply with legal or regulatory requirements</li>
                  <li>For security, fraud, or risk management reasons</li>
                </ul>
                <p>Termination shall not affect accrued rights or obligations.</p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">14. Modifications to Terms</h2>
                <p>
                  ChannelLoyalty reserves the right to amend these Terms at any time. Continued use of the Services after changes constitutes acceptance of the revised Terms.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">15. Governing Law & Jurisdiction</h2>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of India. Courts located in India shall have exclusive jurisdiction over any disputes arising out of or in connection with these Terms.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">16. Contact Information</h2>
                <p>For legal or contractual queries, please contact:</p>
                <p><strong>Email:</strong> <a href="mailto:legal@channelloyalty.ai" className="text-[#2C39B8] hover:underline">legal@channelloyalty.ai</a></p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
    </div>
  );
}
